import { ArpMedia, ArpMediaSubType, ArpMediaType, dedupe, ORDER } from '@arpeggi/shared'
import debounce from 'lodash.debounce'
import { useEffect, useMemo, useState } from 'react'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { useStatus } from '../../../core/Utils'
import { useSearch } from './useSearch'

export type OrderBy = keyof Omit<ArpMedia, 'metadata' | 'subComponents' | 'user'> | 'title'

/** Combination of type and subtype filters. Gives lots of control for filtering sounds. */
export type TypeAndSubTypeFilter = {
  types: ArpMediaType[]
  subTypes: ArpMediaSubType[]
}
export const CLIPS_FILTER: TypeAndSubTypeFilter = {
  types: [ArpMediaType.STEM],
  subTypes: [ArpMediaSubType.CLIP]
}
export const MIDI_FILTER: TypeAndSubTypeFilter = {
  types: [ArpMediaType.STEM],
  subTypes: [ArpMediaSubType.MIDI_INSTRUMENT, ArpMediaSubType.MIDI_DRUM]
}
export const ONE_SHOT_FILTER: TypeAndSubTypeFilter = {
  types: [ArpMediaType.SAMPLE],
  subTypes: [null]
}
export const DEFAULT_TYPE_FILTERS: TypeAndSubTypeFilter[] = [
  { types: [ArpMediaType.STEM, ArpMediaType.SAMPLE], subTypes: [] }
]

export interface SoundsFilters {
  typeAndSubType: TypeAndSubTypeFilter[]
  key: number
  tag: string[]
  orderBy: OrderBy
  order: ORDER
  subComponentId?: number[]
  walletAddress?: string
  featured?: boolean
  shadowBlocklist?: boolean
  ids?: number[]
  samplePackId?: number
}

export const DEFAULT_INSTRUMENTS: string[] = []
export const DEFAULT_ORDER_BY: OrderBy = 'favoriteCount'
export const DEFAULT_ORDER: ORDER = 'desc'
export const DEFAULT_KEY: number | undefined = undefined
export const DEFAULT_WALLET_ADDRESS: string | undefined = undefined
export const DEFAULT_SAMPLE_PACK_ID: number | undefined = undefined

export const DEFAULT_FILTERS: SoundsFilters = {
  key: DEFAULT_KEY,
  typeAndSubType: DEFAULT_TYPE_FILTERS,
  tag: DEFAULT_INSTRUMENTS,
  orderBy: DEFAULT_ORDER_BY,
  order: DEFAULT_ORDER,
  walletAddress: DEFAULT_WALLET_ADDRESS,
  samplePackId: DEFAULT_SAMPLE_PACK_ID
}

const useFilters = (defaultFilters = DEFAULT_FILTERS) => {
  const [filters, setFilters] = useState<SoundsFilters>(defaultFilters)
  const updateFilters = (filter: Partial<SoundsFilters>) => setFilters({ ...filters, ...filter })
  return { filters, updateFilters }
}

const useSounds = (defaultFilters = DEFAULT_FILTERS) => {
  const [search] = useSearch()
  const { filters, updateFilters } = useFilters(defaultFilters)
  const [data, setData] = useState<IArpMedia[]>([])
  const [status, setStatus] = useStatus()
  const [pageData, setPageData] = useState<{ currentPage: number; lastPage: number }>({
    currentPage: 0,
    lastPage: 1
  })
  const hasMore = pageData.currentPage < pageData.lastPage

  const handleSearch = async (filters: SoundsFilters, search: string, fresh = false) => {
    const page = fresh ? 1 : pageData.currentPage + 1
    // A little hacky, but the easiest way to send these compound filters to the backend.
    // Basically deconstruct them into two lists, and pass those up.
    let type = filters.typeAndSubType.flatMap((typeAndSubType) => typeAndSubType.types)
    let subType = filters.typeAndSubType.flatMap((typeAndSubType) => typeAndSubType.subTypes)
    type = dedupe(type)
    subType = dedupe(subType)

    // Remove duplicates
    const { typeAndSubType, ...rest } = filters
    const { media, meta } = await ArpRegistry.search({ search, ...rest, type, subType, page })
    const { lastPage, currentPage } = meta
    setPageData({ currentPage, lastPage })
    setData(fresh ? media : [...data, ...media])
    setStatus('ready')
  }

  const debouncedSearch = useMemo(() => debounce(handleSearch, 500), [])

  useEffect(() => {
    setStatus('loading')
    debouncedSearch(filters, search, true)
  }, [filters, search])

  useEffect(() => {
    return () => debouncedSearch.cancel()
  }, [])

  const getNextPage = () => handleSearch(filters, search)

  return { updateFilters, filters, hasMore, data, getNextPage, status }
}

export const noSoundFilters = (filters: SoundsFilters) => {
  if (filters.key !== DEFAULT_KEY) return false
  if (filters.tag !== DEFAULT_INSTRUMENTS) return false
  if (filters.typeAndSubType !== DEFAULT_TYPE_FILTERS) return false
  return true
}

export default useSounds
